import moment from 'moment';
import format from 'date-fns/format';
import {
  QuestionTextField,
  QuestionBusinessNumberField,
  QuestionRadioButtonsField,
  QuestionTrustAccountNumberField,
  QuestionDropDownField,
  QuestionYesNoField,
  QuestionGiinField,
  QuestionDateDropdownField
} from 'components/Questionnaires/fields';

import {
  QuestionSignerBlock,
  QuestionBlockRepeater,
  QuestionRcUsTaxResidentBlock,
  QuestionRcControllingPersonBlock
} from 'components/Questionnaires/blocks';

import {
  requiredValidator,
  lengthValidator,
  yearMonthValidator
} from 'components/Questionnaires/validators';

import {
  incorporationCountryBlock,
  residentAddressBlock,
  mailingAddressBlock,
  rcTaxResidencesBlock
} from './blocks/blocks';

const rc519 = {
  blocks: [
    {
      id: 'name',
      title: 'questionnaire.rc519.name',
      info: {
        instructions: 'questionnaire.rc519.name.name.info'
      },
      visible: true,
      questions: [
        {
          id: 'name',
          label: 'questionnaire.rc519.name.name.label',
          component: QuestionTextField,
          visible: true,
          lockGroup: 'name',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.name.name.errorMessage' }
            ]
          }
        }
      ]
    },
    incorporationCountryBlock(),
    residentAddressBlock(),
    mailingAddressBlock(),
    {
      id: 'canadianTaxResident',
      title: 'questionnaire.rc519.canadianTaxResident',
      visible: true,
      questions: [
        {
          id: 'isCanadianTaxResident',
          label: 'questionnaire.rc519.canadianTaxResident.isCanadianTaxResident.label',
          component: QuestionYesNoField,
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.canadianTaxResident.isCanadianTaxResident.errorMessage' }
            ]
          }
        },
        {
          id: 'businessNumber',
          label: 'questionnaire.rc519.canadianTaxResident.businessNumber.label',
          component: QuestionBusinessNumberField,
          visible: bAnswers => bAnswers?.isCanadianTaxResident === 'Yes',
          validation: {
            conditions: [
              { condition: lengthValidator, message: 'questionnaire.rc519.canadianTaxResident.businessNumber.lengthErrorMessage' }
            ]
          },
          componentProps: {
            length: 15
          },
        },
        {
          id: 'trustAccountNumber',
          label: 'questionnaire.rc519.canadianTaxResident.trustAccountNumber.label',
          component: QuestionTrustAccountNumberField,
          visible: bAnswers => bAnswers?.isCanadianTaxResident === 'Yes',
          validation: {
            conditions: [
              { condition: lengthValidator, message: 'questionnaire.rc519.canadianTaxResident.trustAccountNumber.lengthErrorMessage' }
            ]
          },
          componentProps: {
            length: 8
          },
          info: {
            instructions: 'questionnaire.rc519.canadianTaxResident.trustAccountNumber.info'
          }
        },
        {
          id: 'residentSinceCanada',
          label: 'questionnaire.rc519.canadianTaxResident.residentSinceCanada.label',
          component: QuestionDateDropdownField,
          componentProps: {
            dateFormat: 'yyyy-MM',
            monthYearOnly: true,
            maxDate: format(moment().toDate(), 'yyyy-MM')
          },
          className: 'col-12 rc-resident-since-date',
          visible: bAnswers => bAnswers?.isCanadianTaxResident === 'Yes',
          info: {
            instructions: 'questionnaire.rc519.canadianTaxResident.residentSinceCanada.info'
          },
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc.residentSince.requiredErrorMessage' },
              { condition: yearMonthValidator, message: 'questionnaire.rc519.canadianTaxResident.residentSinceCanada.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'usTaxResident',
      title: 'questionnaire.rc518.usTaxResident',
      component: QuestionRcUsTaxResidentBlock,
      visible: true,
      info: {
        instructions: 'questionnaire.rc518.usTaxResident.info'
      },
      componentProps: {
        tinType: 'EIN',
        showUsBusinessNumber: true
      }
    },
    rcTaxResidencesBlock(),
    {
      id: 'otherTaxResident',
      title: 'questionnaire.rc519.otherTaxResident',
      visible: formAnswers => Array.isArray(formAnswers?.taxResidences) && formAnswers.taxResidences.some(tr => tr?.country),
      questions: [
        {
          id: 'residentSinceOther',
          label: 'questionnaire.rc519.otherTaxResident.residentSinceOther.label',
          component: QuestionDateDropdownField,
          componentProps: {
            dateFormat: 'yyyy-MM',
            monthYearOnly: true,
            maxDate: format(moment().toDate(), 'yyyy-MM')
          },
          className: 'col-12 rc-resident-since-date',
          visible: (bAnswers, qAnswers) => Array.isArray(qAnswers?.taxResidences) && qAnswers.taxResidences.some(tr => tr?.country),
          info: {
            instructions: 'questionnaire.rc519.otherTaxResident.residentSinceOther.info'
          },
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc.residentSince.requiredErrorMessage' },
              { condition: yearMonthValidator, message: 'questionnaire.rc519.otherTaxResident.residentSinceOther.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'entityType',
      title: 'questionnaire.rc519.entityType',
      visible: true,
      questions: [
        {
          id: 'type',
          label: 'questionnaire.rc519.type.label',
          component: QuestionDropDownField,
          visible: true,
          info: {
            instructions: 'questionnaire.rc519.type.info'
          },
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.type.errorMessage' }
            ]
          },
          componentProps: {
            items: 'questionnaire.rc519.type.items'
          },
        },
        {
          id: 'nonParticipatingJurisdiction',
          label: 'questionnaire.rc519.entityType.financialInstitution.nonParticipatingJurisdiction.label',
          component: QuestionYesNoField,
          visible: bAnswers => bAnswers?.type === '2',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.entityType.financialInstitution.nonParticipatingJurisdiction.errorMessage' }
            ]
          }
        },
        {
          id: 'managedByAnother',
          label: 'questionnaire.rc519.entityType.financialInstitution.managedByAnother.label',
          component: QuestionYesNoField,
          visible: bAnswers => bAnswers?.nonParticipatingJurisdiction === 'Yes',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.entityType.financialInstitution.managedByAnother.errorMessage' }
            ]
          }
        },
        {
          id: 'investingFinancialAssets',
          label: 'questionnaire.rc519.entityType.financialInstitution.investingFinancialAssets.label',
          component: QuestionYesNoField,
          visible: bAnswers => bAnswers?.managedByAnother === 'Yes',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.entityType.financialInstitution.investingFinancialAssets.errorMessage' }
            ]
          }
        },
        {
          id: 'strataCondominium',
          label: 'questionnaire.rc519.entityType.strataCondominium.label',
          component: QuestionYesNoField,
          visible: bAnswers => bAnswers?.type === '1' || bAnswers?.type === '3',
          info: {
            instructions: 'questionnaire.rc519.entityType.strataCondominium.info'
          },
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.entityType.strataCondominium.errorMessage' }
            ]
          }
        },
        {
          id: 'activeNonFinancialEntityType',
          label: 'questionnaire.rc519.entityType.activeNonFinancialEntityType.label',
          component: QuestionRadioButtonsField,
          visible: bAnswers => bAnswers?.type === '1',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.entityType.activeNonFinancialEntityType.errorMessage' }
            ]
          },
          componentProps: {
            items: 'questionnaire.rc519.entityType.activeNonFinancialEntityType.items'
          }
        }
      ]
    },
    {
      id: 'giin',
      title: 'questionnaire.rc519.giin',
      info: {
        instructions: 'questionnaire.rc519.giin.info'
      },
      visible: qAnswers => qAnswers?.entityType?.type === '2',
      questions: [
        {
          id: 'hasGiin',
          label: 'questionnaire.rc519.giin.hasGiin.label',
          component: QuestionYesNoField,
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.giin.hasGiin.errorMessage' }
            ]
          }
        },
        {
          id: 'giin',
          label: 'questionnaire.rc519.giin.giin.label',
          component: QuestionGiinField,
          componentProps: {
            length: 16
          },
          visible: bAnswers => bAnswers?.hasGiin === 'Yes',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.giin.giin.errorMessage' },
              { condition: lengthValidator, message: 'questionnaire.rc519.giin.giin.lengthErrorMessage' }
            ]
          }
        },
        {
          id: 'noGiinReason',
          label: 'questionnaire.rc519.giin.noGiinReason.label',
          component: QuestionTextField,
          visible: bAnswers => bAnswers?.hasGiin === 'No',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc519.giin.noGiinReason.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'controllingPersons',
      title: 'questionnaire.rc519.controllingPersons',
      component: QuestionBlockRepeater,
      info: {
        instructions: 'questionnaire.rc519.controllingPersons.info'
      },
      visible: qAnswers => qAnswers?.entityType?.type === '3' || qAnswers?.entityType?.investingFinancialAssets === 'Yes',
      repeatBlock: {
        id: 'controllingPerson',
        component: QuestionRcControllingPersonBlock
      },
      minSize: 1,
      maxSize: 2,
      addText: 'questionnaire.rc519.controllingPersons.addLabel',
    },
    {
      id: 'signer',
      title: 'questionnaire.signer',
      component: QuestionSignerBlock,
      componentProps: {
        entityOnly: true,
        capacityInfo: 'questionnaire.rc519.signer.capacity.info'
      },
      visible: true
    },
  ]
};

export default rc519;
